import { useState, useEffect, useRef, useMemo } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Ship, MapPin, Clock, Users, Search, CheckCircle2, Anchor,
  ChevronDown, ChevronUp, Download, Gauge, Wind, Waves, Thermometer, Eye,
} from "lucide-react";
import { MOCK_VOYAGES, MOCK_VESSELS, OFFSHORE_ASSETS, VESSEL_STATUS_COLORS } from "@/data/portalMockData";
import { toast } from "sonner";

const statusColors: Record<string, string> = {
  in_progress: "bg-blue-100 text-blue-700",
  scheduled: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const ASSET_TYPE_COLORS: Record<string, string> = {
  FPSO: "#E74C3C",
  Drillship: "#3498DB",
  Terminal: "#FF9F1C",
  "Semi-Sub": "#F39C12",
};

// ── Asset Map ────────────────────────────────────────────────
const AssetMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let map: any;
    const loadMap = async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (!mapRef.current) return;

      map = L.map(mapRef.current, {
        center: [6, -40],
        zoom: 3,
        zoomControl: false,
        attributionControl: false,
      });

      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);
      L.control.zoom({ position: "bottomright" }).addTo(map);

      OFFSHORE_ASSETS.forEach((asset) => {
        const color = ASSET_TYPE_COLORS[asset.type] || "#fff";
        const size = asset.type === "Terminal" ? 18 : 16;
        const opStatus = (asset as any).operatorStatus || "operated";
        let shape: string;

        if (asset.type === "Terminal") {
          const opacity = opStatus === "future" ? "0.4" : opStatus === "non-operated" ? "0.6" : "1";
          shape = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" style="filter:drop-shadow(0 0 4px ${color}80);opacity:${opacity};"><path d="M12 2L12 10M12 10C9.79 10 8 11.79 8 14M12 10C14.21 10 16 11.79 16 14M8 14H5L7 22H17L19 14H16M8 14H16M6 6H18" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        } else if (opStatus === "future") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:transparent;border:2px dashed ${color};box-shadow:0 0 10px ${color}40;${tr}"></div>`;
        } else if (opStatus === "non-operated") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:${color}55;border:2px dashed #fff;box-shadow:0 0 10px ${color}60;${tr}"></div>`;
        } else {
          shape = asset.type === "FPSO"
            ? `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;box-shadow:0 0 12px ${color}80;transform:rotate(45deg);"></div>`
            : `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 12px ${color}80;"></div>`;
        }

        const icon = L.divIcon({ className: "", html: shape, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });

        L.marker([asset.lat, asset.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:200px;">
              <div style="font-weight:700;font-size:14px;margin-bottom:2px;">${asset.name}</div>
              <div style="font-size:11px;color:#888;margin-bottom:6px;">${asset.type} · ${asset.region}</div>
              ${asset.field ? `<div style="font-size:11px;color:#aaa;">Field: ${asset.field}</div>` : ""}
              <div style="font-size:10px;color:#aaa;margin-top:4px;">${Math.abs(asset.lat).toFixed(4)}° ${asset.lat >= 0 ? "N" : "S"}, ${Math.abs(asset.lng).toFixed(4)}° ${asset.lng >= 0 ? "E" : "W"}</div>
            </div>
          `);
      });

      MOCK_VESSELS.forEach((v) => {
        const color = VESSEL_STATUS_COLORS[v.status].bg;
        const icon = L.divIcon({
          className: "",
          html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 8px ${color}80;"></div>`,
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });
        L.marker([v.lat, v.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:180px;">
              <div style="font-weight:700;font-size:14px;">${v.name}</div>
              <div style="font-size:11px;color:#666;">${v.type} · ${v.client}</div>
              <div style="font-size:11px;color:#888;">${VESSEL_STATUS_COLORS[v.status].label} · ${v.maxSpeed} kts</div>
            </div>
          `);
      });

      const allPoints = [
        ...OFFSHORE_ASSETS.map(a => [a.lat, a.lng] as [number, number]),
        ...MOCK_VESSELS.map(v => [v.lat, v.lng] as [number, number]),
      ];
      if (allPoints.length > 0) {
        map.fitBounds(L.latLngBounds(allPoints), { padding: [50, 50], maxZoom: 8 });
      }
      setMapReady(true);
    };
    loadMap();
    return () => { map?.remove(); };
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden border border-border" style={{ height: 400 }}>
      {!mapReady && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
      <div className="absolute bottom-3 left-3 z-[1000] bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 flex flex-wrap gap-3">
        {Object.entries(ASSET_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5">
            {type === "Terminal" ? (
              <span style={{ color, fontSize: 12 }}>⚓</span>
            ) : (
              <div className="w-3 h-3" style={{
                background: color,
                borderRadius: type === "Drillship" ? "50%" : type === "FPSO" ? "0" : "2px",
                transform: type === "FPSO" ? "rotate(45deg) scale(0.7)" : "none",
              }} />
            )}
            <span className="text-[10px] text-white/80">{type === "Terminal" ? "Terminal / Port" : type}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#28A745]" />
          <span className="text-[10px] text-white/80">Vessel</span>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────
const EmployeeVoyageOperations = () => {
  const [search, setSearch] = useState("");
  const [expandedVoyage, setExpandedVoyage] = useState<string | null>(null);
  const [filterVessel, setFilterVessel] = useState("all");

  const { data: vessels = [] } = useQuery({
    queryKey: ["fleet-vessels"],
    queryFn: async () => {
      const { data } = await supabase.from("fleet_vessels").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: crewOnboard = [] } = useQuery({
    queryKey: ["crew-onboard-count"],
    queryFn: async () => {
      const { data } = await supabase.from("crew_schedules").select("*").eq("status", "onboard");
      return data ?? [];
    },
  });

  const { data: surveys = [] } = useQuery({
    queryKey: ["satisfaction-surveys-voyage"],
    queryFn: async () => {
      const { data } = await supabase.from("satisfaction_surveys").select("voyage_ref, rating_punctuality, vessel_name").order("submitted_at", { ascending: false }).limit(50);
      return (data ?? []) as any[];
    },
  });

  const voyages = useMemo(() => {
    return MOCK_VOYAGES.map(v => {
      const dbVessel = vessels.find((dv: any) => dv.name === v.vessel);
      return { ...v, vesselStatus: dbVessel?.status || "unknown", vesselPosition: dbVessel?.current_position || null };
    });
  }, [vessels]);

  const filtered = voyages.filter(v =>
    v.voyageId.toLowerCase().includes(search.toLowerCase()) ||
    v.vessel.toLowerCase().includes(search.toLowerCase()) ||
    v.master.toLowerCase().includes(search.toLowerCase())
  );

  const active = filtered.filter(v => ["in_progress", "confirmed", "scheduled"].includes(v.status));
  const completed = filtered.filter(v => v.status === "completed");

  const filteredHistory = completed.filter(v => {
    const matchesVessel = filterVessel === "all" || v.vessel === filterVessel;
    return matchesVessel;
  });

  const avgPunctuality = surveys.length > 0
    ? (surveys.reduce((sum: number, s: any) => sum + (s.rating_punctuality || 0), 0) / surveys.length).toFixed(1)
    : "—";
  const deployedCount = vessels.filter((v: any) => v.status === "deployed" || v.status === "available").length;

  // Compute fleet-wide avg speed from mock voyages
  const avgFleetSpeed = (MOCK_VOYAGES.reduce((s, v) => s + v.avgSpeedKts, 0) / MOCK_VOYAGES.length).toFixed(1);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Voyage Operations</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Plan, track, and manage all crew transfer voyages</p>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: "Active Voyages", value: active.length.toString(), icon: Ship, color: "text-primary" },
          { label: "Vessels Deployed", value: deployedCount.toString(), icon: Anchor, color: "text-emerald-600" },
          { label: "Crew Onboard", value: crewOnboard.length.toString(), icon: Users, color: "text-blue-600" },
          { label: "Avg Speed", value: avgFleetSpeed + " kts", icon: Gauge, color: "text-amber-600" },
          { label: "Avg Punctuality", value: avgPunctuality + (avgPunctuality !== "—" ? "/5" : ""), icon: CheckCircle2, color: "text-emerald-600" },
        ].map(kpi => (
          <Card key={kpi.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <kpi.icon className={`w-7 h-7 ${kpi.color}`} />
              <div>
                <p className="text-lg font-display text-foreground">{kpi.value}</p>
                <p className="text-[11px] text-muted-foreground font-body">{kpi.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Asset Map */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              <CardTitle className="text-base font-display">Offshore Assets & Vessel Tracking</CardTitle>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {OFFSHORE_ASSETS.length} assets · {MOCK_VESSELS.length} vessels
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <AssetMap />
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search voyages..." className="pl-10" />
      </div>

      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active">Active & Scheduled ({active.length})</TabsTrigger>
          <TabsTrigger value="history">Completed ({completed.length})</TabsTrigger>
        </TabsList>

        {/* Active Voyages — expandable cards */}
        <TabsContent value="active" className="space-y-3 mt-4">
          {active.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><Anchor className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No active voyages</p></CardContent></Card>
          ) : active.map(v => (
            <Card key={v.voyageId} className="hover:shadow-md transition-shadow overflow-hidden">
              <div
                className="p-5 flex items-center gap-4 cursor-pointer"
                onClick={() => setExpandedVoyage(expandedVoyage === v.id ? null : v.id)}
              >
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${v.status === "in_progress" ? "bg-blue-500 animate-pulse" : v.status === "confirmed" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base text-foreground">{v.voyageId}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusColors[v.status] || "bg-muted text-muted-foreground"}`}>{v.status.replace("_", " ")}</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-body mt-1">{v.vessel} · {v.master} · {v.client}</p>
                </div>
                <div className="hidden md:flex items-center gap-6 text-center">
                  <div><p className="text-sm font-bold text-foreground">{v.pax}</p><p className="text-[9px] text-muted-foreground">PAX</p></div>
                  <div><p className="text-sm font-bold text-foreground">{v.avgSpeedKts} kts</p><p className="text-[9px] text-muted-foreground">Avg Speed</p></div>
                  <div><p className="text-sm font-bold text-foreground">{new Date(v.departureTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p><p className="text-[9px] text-muted-foreground">Depart</p></div>
                  <div><p className="text-sm font-bold text-foreground">{new Date(v.eta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p><p className="text-[9px] text-muted-foreground">ETA</p></div>
                </div>
                {expandedVoyage === v.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>

              {expandedVoyage === v.id && (
                <div className="px-5 pb-5 pt-0 border-t bg-muted/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-3 text-xs">
                    <div><span className="text-muted-foreground">Master:</span> <span className="font-medium">{v.master}</span></div>
                    <div><span className="text-muted-foreground">Duration:</span> <span className="font-medium">{v.duration}</span></div>
                    <div><span className="text-muted-foreground">Fuel Est.:</span> <span className="font-medium">{v.fuelMT} MT</span></div>
                    <div className="flex items-center gap-1"><Gauge className="w-3 h-3 text-primary" /><span className="text-muted-foreground">Avg Speed:</span> <span className="font-medium">{v.avgSpeedKts} kts</span></div>
                    <div><span className="text-muted-foreground">Route:</span> <span className="font-medium">{v.origin} → {v.destination}</span></div>
                    {v.cargo && <div className="col-span-2"><span className="text-muted-foreground">Cargo:</span> <span className="font-medium">{v.cargoDesc} ({v.cargoWeight} MT)</span></div>}
                  </div>
                  {v.vesselPosition && (
                    <p className="text-[10px] text-primary font-body mb-2 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Current position: {v.vesselPosition}
                    </p>
                  )}
                  {/* Weather & Sea State Panel */}
                  <div className="mt-1 p-3 rounded-lg bg-background border">
                    <p className="text-[10px] font-bold tracking-[2px] uppercase text-muted-foreground mb-2">
                      {v.status === "completed" ? "Recorded Conditions" : v.status === "in_progress" ? "Current Conditions" : "Forecast Conditions"}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <Waves className="w-3.5 h-3.5 text-primary" />
                        <div><p className="text-muted-foreground">Sig. Wave Ht</p><p className="font-bold text-foreground">{v.weatherHs} m</p></div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Wind className="w-3.5 h-3.5 text-primary" />
                        <div><p className="text-muted-foreground">Wind</p><p className="font-bold text-foreground">{v.weatherWind} kts</p></div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Anchor className="w-3.5 h-3.5 text-primary" />
                        <div><p className="text-muted-foreground">Sea State</p><p className="font-bold text-foreground">{v.seaState}</p></div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Thermometer className="w-3.5 h-3.5 text-primary" />
                        <div><p className="text-muted-foreground">Water Temp</p><p className="font-bold text-foreground">{v.waterTempC}°C</p></div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Eye className="w-3.5 h-3.5 text-primary" />
                        <div><p className="text-muted-foreground">Visibility</p><p className="font-bold text-foreground">{v.visibilityNM} NM</p></div>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">Conditions: <span className="font-medium text-foreground">{v.weatherCondition}</span></p>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </TabsContent>

        {/* Completed — full table */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <div className="p-4 flex flex-wrap items-center gap-3 border-b">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search history..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
              </div>
              <Select value={filterVessel} onValueChange={setFilterVessel}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vessels</SelectItem>
                  {MOCK_VESSELS.map(v => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => toast.success("Generating: Export CSV", { description: "Your file will download shortly." })} variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    {["Voyage ID", "Date", "Vessel", "Route", "Master", "PAX", "Avg Speed", "Sea State", "Wind", "Hs", "Conditions", "Score"].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length === 0 ? (
                    <tr><td colSpan={12} className="px-4 py-12 text-center text-muted-foreground font-body">No completed voyages yet</td></tr>
                  ) : filteredHistory.map(v => (
                    <tr key={v.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-3 py-2.5 font-medium text-foreground">{v.voyageId}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{new Date(v.departureTime).toLocaleDateString()}</td>
                      <td className="px-3 py-2.5">{v.vessel}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{v.origin} → {v.destination}</td>
                      <td className="px-3 py-2.5">{v.master}</td>
                      <td className="px-3 py-2.5 text-center">{v.pax}</td>
                      <td className="px-3 py-2.5 text-center font-medium">{v.avgSpeedKts} kts</td>
                      <td className="px-3 py-2.5">{v.seaState}</td>
                      <td className="px-3 py-2.5 text-center">{v.weatherWind} kts</td>
                      <td className="px-3 py-2.5 text-center">{v.weatherHs} m</td>
                      <td className="px-3 py-2.5">{v.weatherCondition}</td>
                      <td className="px-3 py-2.5 text-center">{v.satScore > 0 ? `${v.satScore} ⭐` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
};

export const EmployeeVoyageOperationsContent = EmployeeVoyageOperations;

const EmployeeVoyageOperationsPage = () => (
  <IntranetLayout>
    <EmployeeVoyageOperationsContent />
  </IntranetLayout>
);

export default EmployeeVoyageOperationsPage;
